import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { Form, FormInstance } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { usePolicies } from '@hooks/usePolicies';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useMutation } from '@tanstack/react-query';
import { CognitoUser } from 'aws-amplify';

export type RAStage = 'preparing' | 'ready' | 'submitting' | 'invalid' | 'error' | 'success';

type UseGuestAccessLoginReturn = {
  stage: RAStage;
  form: FormInstance;
  onFinish: () => Promise<void>;
};

const useGuestAccessLogin = (): UseGuestAccessLoginReturn => {
  const dispatch = useDispatch();

  const { push, query } = useRouter();
  const { guid, token, url, appt_id } = query as Record<string, string>;

  const [form] = Form.useForm();

  const [user, setUser] = useState<CognitoUser | undefined>(undefined);
  const [stage, setStage] = useState<RAStage>('preparing');
  const { policiesAccepted } = usePolicies();

  const fetchUser = async () => {
    const user = await dispatch(actions.userSendToken(String(guid), String(token)));
    return user as CognitoUser;
  };

  const { mutateAsync: getUser } = useMutation({
    mutationFn: fetchUser,
    onSuccess: (user) => {
      setUser(user);
      setStage('ready');
    },
    onError: (error) => {
      setStage('invalid');
    }
  });

  useEffect(() => {
    getUser().then();
  }, [getUser]);

  const sendLoginAnswerAndFetchUser = async ({ dob, membershipNumber }: { dob: string; membershipNumber: string }) => {
    const removeSpaces = (value: string): string => value.replace(/\s/g, '');
    return dispatch(
      actions.userLinkLogin(
        dob,
        removeSpaces(membershipNumber),
        { source_url: window.location.href },
        user as CognitoUser
      )
    );
  };

  const redirectOnSuccess = () => {
    if (url && appt_id) {
      const encodedApptId = Buffer.from(`${appt_id}`).toString('base64');
      push(`${url}/?appt=${encodedApptId}`).then();
      return;
    }
    if (!policiesAccepted) {
      push('/policy').then();
      return;
    }

    push('/patients?afterLogin=true').then();
  };

  const { mutateAsync: sendChallenge } = useMutation({
    mutationFn: sendLoginAnswerAndFetchUser,
    onSuccess: (response) => {
      const linkLoginSucceeded = response?.type === actionTypes.AUTH_SUCCESS;
      if (linkLoginSucceeded) {
        setStage('success');
        redirectOnSuccess();
      } else {
        setStage('error');
      }
    },
    onError: (error: { type?: string }) => {
      if (error?.type === actionTypes.AUTH_LOGIN_FAIL) {
        setStage('invalid');
        return;
      }
      setStage('error');
    }
  });

  const sendRestrictedAccessChallenge = useCallback(async () => {
    if (!user) {
      setStage('invalid');
      return;
    }

    const { dob, membershipNumber } = form.getFieldsValue();

    setStage('submitting');
    try {
      await sendChallenge({ dob, membershipNumber });
    } catch (error) {
      console.error('Error submitting challenge:', error);
    }
  }, [form, sendChallenge, user]);

  return {
    stage,
    form,
    onFinish: sendRestrictedAccessChallenge
  };
};

export default useGuestAccessLogin;
